import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { useToast } from '../ui/use-toast';
import { useUserVFS } from '../../hooks/useUserVFS';
import { Folder, Shield, Play, StopCircle, Upload, Search, CheckCircle, AlertTriangle, Globe, Cpu, Sparkles, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ensureDir, ensureFile } from '../../utils/filesystem';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

type ModuleType = 'web' | 'wasm' | 'engine-plugin';
type ModuleLoaderKind = 'bundle' | 'package';
type ModulePermission =
  | 'files:read'
  | 'files:write'
  | 'windows:open'
  | 'notifications:show'
  | 'network:proxy'
  | 'ai:maplept';

type MapleCodeWasmRunResultDetail = {
  projectPath: string;
  moduleName: string;
  status: 'success' | 'error';
  runId: number;
  lines: string[];
  sourcePath?: string;
};

type OpenWindowHandler = (appId: string, title: string, filePath?: string, content?: string) => void;

type ModuleBridgeRequest = {
  __mapleHostBridge: true;
  type: 'request';
  id: string;
  method: string;
  params?: Record<string, unknown>;
};

type ModuleBridgeResponse = {
  __mapleHostBridge: true;
  type: 'response';
  id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
};

interface ModuleManifest {
  name: string;
  version: string;
  description?: string;
  author?: string;
  categories?: string[];
  permissions?: ModulePermission[];
  aiCapability?: boolean;
  mapleOSVersion?: string;
  type: ModuleType;
  loader?: ModuleLoaderKind;
  assets?: string;
  entry?: {
    html?: string;
    js?: string;
    wasm?: string;
    plugin?: string;
  };
  icons?: { small?: string; large?: string };
  screenshots?: string[];
  marketplace?: {
    sku?: string;
    packageType?: string;
    installedAt?: string;
  };
}

interface InstalledModule {
  id: string;
  rootPath: string;
  manifestPath: string;
  manifest: ModuleManifest;
  installed: boolean;
}

interface ModuleHostProps {
  initialProjectPath?: string;
  onOpenWindow?: OpenWindowHandler;
}

const PROGRAM_FILES_MODULES = 'C:\\Program Files\\Modules';
const PROJECTS_ROOT = 'C:\\Projects';
const HELLO_WORLD_WAT = `(module
  (import "env" "log_utf8" (func $log_utf8 (param i32 i32)))
  (memory (export "memory") 1)
  (data (i32.const 0) "Hello, world!")
  (func $hello (export "hello")
    i32.const 0
    i32.const 13
    call $log_utf8)
  (func (export "_start")
    call $hello))`;

const BRIDGE_BOOTSTRAP_SCRIPT = String.raw`<script>
(() => {
  if (window.MapleOS && window.MapleOS.__bridgeVersion === 1) {
    return;
  }

  const pending = new Map();
  let nextId = 1;

  const request = (method, params = {}) => new Promise((resolve, reject) => {
    const id = 'maple-host-' + nextId++;
    pending.set(id, { resolve, reject });
    window.parent.postMessage({
      __mapleHostBridge: true,
      type: 'request',
      id,
      method,
      params,
    }, '*');
  });

  const decorateFileResult = (result) => {
    if (!result || typeof result !== 'object') {
      return result;
    }

    try {
      Object.defineProperty(result, 'toString', {
        value() {
          return typeof result.content === 'string' ? result.content : '';
        },
        enumerable: false,
      });
      Object.defineProperty(result, Symbol.toPrimitive, {
        value() {
          return typeof result.content === 'string' ? result.content : '';
        },
        enumerable: false,
      });
    } catch {}

    return result;
  };

  const decorateListResult = (result) => {
    if (!result || typeof result !== 'object') {
      return result;
    }

    const entries = Array.isArray(result.entries) ? result.entries : [];
    const names = entries.map((entry) => typeof entry?.name === 'string' ? entry.name : String(entry?.path || ''));

    try {
      Object.defineProperty(result, 'join', {
        value(separator = ', ') {
          return names.join(separator);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'map', {
        value(callback) {
          return entries.map(callback);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'forEach', {
        value(callback) {
          return entries.forEach(callback);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'filter', {
        value(callback) {
          return entries.filter(callback);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'length', {
        get() {
          return entries.length;
        },
        enumerable: false,
      });
      Object.defineProperty(result, Symbol.iterator, {
        value: function* () {
          yield* entries;
        },
        enumerable: false,
      });
    } catch {}

    return result;
  };

  window.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || data.__mapleHostBridge !== true || data.type !== 'response' || typeof data.id !== 'string') {
      return;
    }

    const entry = pending.get(data.id);
    if (!entry) {
      return;
    }

    pending.delete(data.id);
    if (data.ok) {
      entry.resolve(data.result);
    } else {
      entry.reject(new Error(data.error || 'MapleOS host request failed.'));
    }
  });

  const api = {
    __bridgeVersion: 1,
    app: {
      getInfo: () => request('app.getInfo'),
    },
    notifications: {
      show: (options) => request('notifications.show', options || {}),
    },
    windows: {
      open: (options) => request('windows.open', options || {}),
    },
    fs: {
      readFile: async (path) => decorateFileResult(await request('fs.readFile', { path })),
      writeFile: (path, content, options = {}) => request('fs.writeFile', { path, content, ...options }),
      mkdir: (path) => request('fs.mkdir', { path }),
      list: async (path) => decorateListResult(await request('fs.list', { path })),
    },
    host: {
      log: (...args) => request('host.log', { parts: args.map((value) => {
        if (typeof value === 'string') return value;
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }) }),
    },
  };

  window.MapleOS = api;
  window.mapleOS = api;
  window.dispatchEvent(new CustomEvent('mapleos-ready', { detail: { version: 1 } }));
})();
</script>`;

const humanizePermission = (perm: ModulePermission) => {
  switch (perm) {
    case 'files:read':
      return 'Read files';
    case 'files:write':
      return 'Write files';
    case 'windows:open':
      return 'Open windows';
    case 'notifications:show':
      return 'Show notifications';
    case 'network:proxy':
      return 'Network (proxied)';
    case 'ai:maplept':
      return 'AI: MaplePT';
    default:
      return perm;
  }
};

const ModuleHost: React.FC<ModuleHostProps> = ({ initialProjectPath, onOpenWindow }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { files, isLoaded, listFiles, getFile, createFile, updateFile, deleteFile, reloadFile } = useUserVFS();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [modules, setModules] = useState<InstalledModule[]>([]);
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const [activeHtml, setActiveHtml] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [runtimeKind, setRuntimeKind] = useState<'web' | 'wasm' | null>(null);
  const [wasmExports, setWasmExports] = useState<Record<string, any> | null>(null);
  const [wasmLogs, setWasmLogs] = useState<string[]>([]);
  const [activeWasmPath, setActiveWasmPath] = useState<string | null>(null);
  const [mirrorToTerminal, setMirrorToTerminal] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastWasmSigRef = useRef<string>('');
  const warnedOutOfDateRef = useRef(false);
  const hasAutoRanRef = useRef(false);
  const runCounterRef = useRef(0);
  const mirroredRunIdRef = useRef<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<InstalledModule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const normalizePath = useCallback((path: string) => path.replace(/\//g, '\\').replace(/\\\\+/g, '\\'), []);

  const normalizedInitialProjectPath = useMemo(
    () => (initialProjectPath ? normalizePath(initialProjectPath) : null),
    [initialProjectPath, normalizePath],
  );

  const initialPathIsInstalledModule = useMemo(
    () => Boolean(normalizedInitialProjectPath && normalizedInitialProjectPath.startsWith(`${PROGRAM_FILES_MODULES}\\`)),
    [normalizedInitialProjectPath],
  );

  const getRelativePath = useCallback(
    (rootPath: string, targetPath: string) => {
      const normalizedRoot = normalizePath(rootPath);
      const normalizedTarget = normalizePath(targetPath);
      return normalizedTarget.startsWith(`${normalizedRoot}\\`)
        ? normalizedTarget.slice(normalizedRoot.length + 1)
        : normalizedTarget;
    },
    [normalizePath],
  );

  const getActiveModule = useCallback(
    () => modules.find(module => module.id === activeModuleId) || null,
    [activeModuleId, modules],
  );

  const hasModulePermission = useCallback((mod: InstalledModule, permission: ModulePermission) => {
    return (mod.manifest.permissions || []).includes(permission);
  }, []);

  const resolveModuleFilePath = useCallback(
    (mod: InstalledModule, value: unknown) => {
      const raw = String(value || '').trim();
      if (!raw) {
        throw new Error('Path is required.');
      }

      if (/^[a-z]:\\/i.test(raw) || /^[a-z]:$/i.test(raw)) {
        return normalizePath(raw);
      }

      return normalizePath(`${mod.rootPath}\\${raw.replace(/^\\+/, '')}`);
    },
    [normalizePath],
  );

  const injectDesktopBridge = useCallback((html: string) => {
    if (html.includes('__mapleHostBridge') || html.includes('__bridgeVersion === 1')) {
      return html;
    }

    if (/<\/head>/i.test(html)) {
      return html.replace(/<\/head>/i, `${BRIDGE_BOOTSTRAP_SCRIPT}</head>`);
    }

    if (/<body[^>]*>/i.test(html)) {
      return html.replace(/<body([^>]*)>/i, `<body$1>${BRIDGE_BOOTSTRAP_SCRIPT}`);
    }

    return `${BRIDGE_BOOTSTRAP_SCRIPT}${html}`;
  }, []);

  const postBridgeResponse = useCallback((id: string, ok: boolean, payload: unknown, target: Window) => {
    const response: ModuleBridgeResponse = {
      __mapleHostBridge: true,
      type: 'response',
      id,
      ok,
      ...(ok ? { result: payload } : { error: String(payload || 'MapleOS host request failed.') }),
    };
    target.postMessage(response, '*');
  }, []);

  const log = useCallback((msg: string) => {
    setWasmLogs(prev => [...prev, msg]);
  }, []);

  const decodeBase64 = (b64: string) => {
    const clean = b64.startsWith('data:') ? b64.split(',')[1] ?? '' : b64.trim();
    const bin = atob(clean);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
      bytes[i] = bin.charCodeAt(i);
    }
    return bytes;
  };

  const bytesToBase64 = (u8: Uint8Array) => {
    let binary = '';
    for (let i = 0; i < u8.length; i += 1) {
      binary += String.fromCharCode(u8[i]);
    }
    return btoa(binary);
  };

  const compileWatToWasm = async (watSource: string): Promise<Uint8Array> => {
    const wabtFactory = (await import('wabt')).default as any;
    const wabtInstance = await wabtFactory();
    const parsed = wabtInstance.parseWat('module.wat', watSource);
    const binary = parsed.toBinary({ log: false });
    return new Uint8Array(binary.buffer);
  };

  const ensureModuleDir = useCallback(
    async (dirPath: string) => {
      const normalized = normalizePath(dirPath);
      await createFile(normalized, '', true);
      await ensureDir(normalized);
    },
    [createFile, normalizePath],
  );

  const writeModuleFile = useCallback(
    async (filePath: string, content: string, contentType = 'text/plain') => {
      const normalized = normalizePath(filePath);
      const existing = getFile(normalized);

      if (existing) {
        await updateFile(normalized, content);
      } else {
        const lastSlash = normalized.lastIndexOf('\\');
        if (lastSlash > 2) {
          await ensureModuleDir(normalized.slice(0, lastSlash));
        }
        await createFile(normalized, content, false, contentType);
      }

      await ensureFile(normalized, content);
      return (await reloadFile(normalized)) ?? getFile(normalized);
    },
    [createFile, ensureModuleDir, getFile, normalizePath, reloadFile, updateFile],
  );

  const inferModuleName = useCallback(
    (rootPath: string) => {
      const normalizedRoot = normalizePath(rootPath);
      const projectFile = getFile(`${normalizedRoot}\\.project`);

      if (projectFile?.content) {
        try {
          const parsed = JSON.parse(projectFile.content);
          if (typeof parsed?.name === 'string' && parsed.name.trim()) {
            return parsed.name.trim();
          }
        } catch {
          // Ignore malformed .project metadata.
        }
      }

      return normalizedRoot.split('\\').filter(Boolean).pop() || 'WASM Module';
    },
    [getFile, normalizePath],
  );

  const ensureStarterDevModule = useCallback(
    async (projectPath: string) => {
      const rootPath = normalizePath(projectPath);
      const manifestPath = `${rootPath}\\manifest.json`;
      const existingManifest = getFile(manifestPath);

      if (existingManifest?.content) {
        return existingManifest;
      }

      const manifest: ModuleManifest = {
        name: inferModuleName(rootPath),
        version: '0.1.0',
        type: 'wasm',
        entry: {
          wasm: 'build/module.wasm',
        },
      };

      await ensureModuleDir(`${rootPath}\\src`);
      await ensureModuleDir(`${rootPath}\\build`);
      await writeModuleFile(manifestPath, JSON.stringify(manifest, null, 2), 'application/json');

      const watPath = `${rootPath}\\build\\module.wat`;
      const existingWat = getFile(watPath);
      if (!existingWat?.content?.trim()) {
        await writeModuleFile(watPath, HELLO_WORLD_WAT, 'text/plain');
      }

      return (await reloadFile(manifestPath)) ?? getFile(manifestPath);
    },
    [ensureModuleDir, getFile, inferModuleName, normalizePath, reloadFile, writeModuleFile],
  );

  const createRunSummaryScript = useCallback(
    async (mod: InstalledModule, lines: string[]) => {
      const buildDir = `${normalizePath(mod.rootPath)}\\build`;
      const batPath = `${buildDir}\\run-output.bat`;
      const batLines = ['@echo off', 'echo MapleOS Module Host run summary', 'echo.'];

      lines.forEach(line => {
        const normalizedLine = line.replace(/\r?\n/g, ' ');
        if (!normalizedLine.trim()) {
          batLines.push('echo.');
        } else {
          batLines.push(`echo ${normalizedLine}`);
        }
      });

      await ensureModuleDir(buildDir);
      await writeModuleFile(batPath, batLines.join('\n'));
      return batPath;
    },
    [ensureModuleDir, normalizePath, writeModuleFile],
  );

  const mirrorRunSummaryToTerminal = useCallback(
    async (mod: InstalledModule, lines: string[], runId: number) => {
      if (!mirrorToTerminal || !onOpenWindow || mirroredRunIdRef.current === runId) {
        return;
      }

      const batPath = await createRunSummaryScript(mod, lines);
      mirroredRunIdRef.current = runId;
      onOpenWindow('terminal', 'Terminal - Module Output', batPath);
    },
    [createRunSummaryScript, mirrorToTerminal, onOpenWindow],
  );

  const publishWasmRunResult = useCallback(
    (mod: InstalledModule, status: 'success' | 'error', lines: string[], runId: number, sourcePath?: string) => {
      window.dispatchEvent(
        new CustomEvent<MapleCodeWasmRunResultDetail>('maplecode:wasm-run-result', {
          detail: {
            projectPath: normalizePath(mod.rootPath),
            moduleName: mod.manifest.name,
            status,
            runId,
            lines,
            sourcePath: sourcePath ? normalizePath(sourcePath) : undefined,
          },
        }),
      );
    },
    [normalizePath],
  );

  const loadInstalledModules = useCallback(() => {
    try {
      const all = listFiles(PROGRAM_FILES_MODULES);
      const folders = all.filter(f => f.is_directory);
      const discovered: InstalledModule[] = [];

      folders.forEach(folder => {
        const manifestPath = `${folder.file_path}\\manifest.json`;
        const manifestFile = getFile(manifestPath);
        if (!manifestFile?.content) {
          return;
        }

        try {
          const manifest = JSON.parse(manifestFile.content) as ModuleManifest;
          discovered.push({
            id: folder.file_path,
            rootPath: folder.file_path,
            manifestPath,
            manifest,
            installed: true,
          });
        } catch {
          // Ignore malformed manifest files.
        }
      });

      setModules(discovered);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load installed modules.',
        variant: 'destructive',
      });
    }
  }, [getFile, listFiles, toast]);

  const loadDevModulesFromProjects = useCallback(() => {
    try {
      const devs: InstalledModule[] = [];
      const seen = new Set<string>();

      const walk = (dir: string) => {
        const items = listFiles(dir);
        const manifestPath = `${dir}\\manifest.json`;
        const manifestFile = getFile(manifestPath);

        if (manifestFile?.content) {
          try {
            const manifest = JSON.parse(manifestFile.content) as ModuleManifest;
            const projectPath = `${dir}\\.project`;
            const projectFile = getFile(projectPath);

            if (projectFile?.content) {
              try {
                const projectData = JSON.parse(projectFile.content);
                if ((manifest.name === 'My WASM Module' || manifest.name === 'My Web Module') && projectData.name) {
                  manifest.name = projectData.name;
                }
                if (!manifest.description && projectData.description) {
                  manifest.description = projectData.description;
                }
              } catch {
                // Ignore malformed .project metadata.
              }
            }

            const id = `${dir}::dev`;
            if (!seen.has(id)) {
              devs.push({
                id,
                rootPath: dir,
                manifestPath,
                manifest,
                installed: false,
              });
              seen.add(id);
            }
          } catch {
            // Ignore malformed manifests.
          }
        }

        items.filter(item => item.is_directory).forEach(subdir => walk(subdir.file_path));
      };

      try {
        void createFile(PROJECTS_ROOT, '', true);
      } catch {
        // Ignore directory init failures.
      }

      walk(PROJECTS_ROOT);
      setModules(prev => {
        const merged = new Map(prev.map(module => [module.id, module]));
        devs.forEach(module => merged.set(module.id, module));
        return Array.from(merged.values());
      });
    } catch {
      // Silent by design.
    }
  }, [createFile, getFile, listFiles]);

  const loadDevModule = useCallback(async () => {
    if (!initialProjectPath || initialPathIsInstalledModule) {
      return;
    }

    try {
      const rootPath = normalizePath(initialProjectPath);
      const manifestPath = `${rootPath}\\manifest.json`;
      let manifestFile = getFile(manifestPath);

      if (!manifestFile?.content) {
        manifestFile = await ensureStarterDevModule(rootPath);
      }

      if (!manifestFile?.content) {
        return;
      }

      const manifest = JSON.parse(manifestFile.content) as ModuleManifest;
      setModules(prev => {
        const devModule: InstalledModule = {
          id: `${rootPath}::dev`,
          rootPath,
          manifestPath,
          manifest,
          installed: false,
        };

        return prev.some(module => module.id === devModule.id)
          ? prev.map(module => (module.id === devModule.id ? devModule : module))
          : [devModule, ...prev];
      });
    } catch {
      // Ignore malformed manifests in dev mode.
    }
  }, [ensureStarterDevModule, getFile, initialPathIsInstalledModule, initialProjectPath, normalizePath]);

  useEffect(() => {
    try {
      void createFile(PROGRAM_FILES_MODULES, '', true);
    } catch {
      // Ignore directory init failures.
    }

    void ensureDir(PROGRAM_FILES_MODULES);
    loadInstalledModules();
    void loadDevModule();

    try {
      void createFile(PROJECTS_ROOT, '', true);
    } catch {
      // Ignore directory init failures.
    }

    loadDevModulesFromProjects();
  }, [createFile, loadDevModule, loadDevModulesFromProjects, loadInstalledModules]);

  const allCategories = useMemo(() => {
    const categories = new Set<string>();
    modules.forEach(module => (module.manifest.categories || []).forEach(category => categories.add(category)));
    return Array.from(categories);
  }, [modules]);

  const filtered = useMemo(
    () =>
      modules.filter(module => {
        const matchesSearch =
          module.manifest.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (module.manifest.description || '').toLowerCase().includes(searchQuery.toLowerCase());
        const categories = module.manifest.categories || [];
        const matchesCategory = categoryFilter === 'all' || categories.includes(categoryFilter);
        return matchesSearch && matchesCategory;
      }),
    [categoryFilter, modules, searchQuery],
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadInstalledModules();
      loadDevModulesFromProjects();
      void loadDevModule();
    }, 200);

    return () => clearTimeout(timeout);
  }, [files, loadDevModule, loadDevModulesFromProjects, loadInstalledModules]);

  useEffect(() => {
    const handleBridgeMessage = async (event: MessageEvent<ModuleBridgeRequest>) => {
      const activeFrame = iframeRef.current;
      const activeModule = getActiveModule();
      if (!activeFrame || !activeModule || runtimeKind !== 'web') {
        return;
      }

      if (event.source !== activeFrame.contentWindow) {
        return;
      }

      const data = event.data;
      if (!data || data.__mapleHostBridge !== true || data.type !== 'request' || typeof data.id !== 'string') {
        return;
      }

      const respond = (ok: boolean, payload: unknown) => {
        if (!activeFrame.contentWindow) {
          return;
        }
        postBridgeResponse(data.id, ok, payload, activeFrame.contentWindow);
      };

      try {
        switch (data.method) {
          case 'app.getInfo': {
            respond(true, {
              name: activeModule.manifest.name,
              version: activeModule.manifest.version,
              description: activeModule.manifest.description || '',
              rootPath: activeModule.rootPath,
              permissions: activeModule.manifest.permissions || [],
            });
            return;
          }
          case 'host.log': {
            const parts = Array.isArray(data.params?.parts) ? data.params?.parts : [];
            log(`[${activeModule.manifest.name}] ${parts.map(part => String(part)).join(' ')}`.trim());
            respond(true, { ok: true });
            return;
          }
          case 'notifications.show': {
            if (!hasModulePermission(activeModule, 'notifications:show')) {
              throw new Error('Permission denied: notifications:show');
            }

            const title = String(data.params?.title || activeModule.manifest.name).trim() || activeModule.manifest.name;
            const description = String(data.params?.message || data.params?.description || '').trim();
            const rawVariant = String(
              data.params?.variant
              || data.params?.type
              || data.params?.status
              || data.params?.intent
              || data.params?.kind
              || ''
            ).trim().toLowerCase();
            const variant = ['destructive', 'error', 'danger', 'failure', 'failed'].includes(rawVariant)
              ? 'destructive'
              : 'default';
            toast({ title, description: description || undefined, variant });
            respond(true, { shown: true, variant });
            return;
          }

          case 'windows.open': {
            if (!hasModulePermission(activeModule, 'windows:open')) {
              throw new Error('Permission denied: windows:open');
            }
            if (!onOpenWindow) {
              throw new Error('Window opening is unavailable in this session.');
            }

            const appId = String(data.params?.appId || '').trim();
            const title = String(data.params?.title || activeModule.manifest.name).trim() || activeModule.manifest.name;
            if (!appId) {
              throw new Error('appId is required.');
            }

            const filePath = typeof data.params?.filePath === 'string' ? data.params.filePath : undefined;
            const content = typeof data.params?.content === 'string' ? data.params.content : undefined;
            onOpenWindow(appId, title, filePath, content);
            respond(true, { opened: true });
            return;
          }
          case 'fs.readFile': {
            if (!hasModulePermission(activeModule, 'files:read')) {
              throw new Error('Permission denied: files:read');
            }

            const targetPath = resolveModuleFilePath(activeModule, data.params?.path);
            const file = (await reloadFile(targetPath)) ?? getFile(targetPath);
            if (!file || file.is_directory) {
              throw new Error(`File not found: ${targetPath}`);
            }

            respond(true, {
              path: targetPath,
              content: file.content || '',
              contentType: file.content_type || 'text/plain',
              updatedAt: file.updated_at || null,
            });
            return;
          }
          case 'fs.writeFile': {
            if (!hasModulePermission(activeModule, 'files:write')) {
              throw new Error('Permission denied: files:write');
            }

            const targetPath = resolveModuleFilePath(activeModule, data.params?.path);
            const content = String(data.params?.content ?? '');
            const contentType = typeof data.params?.contentType === 'string' && data.params.contentType.trim()
              ? data.params.contentType
              : 'text/plain';
            await writeModuleFile(targetPath, content, contentType);
            respond(true, { path: targetPath, saved: true });
            return;
          }
          case 'fs.mkdir': {
            if (!hasModulePermission(activeModule, 'files:write')) {
              throw new Error('Permission denied: files:write');
            }

            const targetPath = resolveModuleFilePath(activeModule, data.params?.path);
            await ensureModuleDir(targetPath);
            respond(true, { path: targetPath, created: true });
            return;
          }
          case 'fs.list': {
            if (!hasModulePermission(activeModule, 'files:read')) {
              throw new Error('Permission denied: files:read');
            }

            const targetPath = resolveModuleFilePath(activeModule, data.params?.path || activeModule.rootPath);
            const entries = listFiles(targetPath);
            respond(true, {
              path: targetPath,
              entries: entries.map(entry => ({
                path: entry.file_path,
                name: entry.file_path.split('\\').filter(Boolean).pop() || entry.file_path,
                isDirectory: entry.is_directory,
                contentType: entry.content_type || null,
                updatedAt: entry.updated_at || null,
              })),
            });
            return;
          }
          default:
            throw new Error(`Unknown host method: ${data.method}`);
        }
      } catch (error) {
        respond(false, error instanceof Error ? error.message : 'MapleOS host request failed.');
      }
    };

    window.addEventListener('message', handleBridgeMessage as EventListener);
    return () => window.removeEventListener('message', handleBridgeMessage as EventListener);
  }, [getActiveModule, getFile, hasModulePermission, listFiles, log, onOpenWindow, postBridgeResponse, reloadFile, resolveModuleFilePath, runtimeKind, toast, writeModuleFile]);

  const requestDeleteModule = (mod: InstalledModule) => {
    setModuleToDelete(mod);
    setDeleteOpen(true);
  };

  const performDeleteModule = async () => {
    if (!moduleToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      const basePath = moduleToDelete.rootPath.replace(/\//g, '\\');
      const affectedFiles = files
        .filter(file => file.file_path === basePath || file.file_path.startsWith(`${basePath}\\`))
        .sort((a, b) => b.file_path.length - a.file_path.length);

      let failures = 0;
      for (const file of affectedFiles) {
        const ok = await deleteFile(file.file_path);
        if (!ok) {
          failures += 1;
        }
      }

      if (failures === 0) {
        toast({
          title: 'Project deleted',
          description: `${moduleToDelete.manifest.name} was removed.`,
        });
      } else {
        toast({
          title: 'Delete incomplete',
          description: 'Some files could not be removed. Try again or refresh.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete project.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
      setModuleToDelete(null);
    }
  };

  const renderPermissionBadges = (manifest: ModuleManifest) => {
    const permissions = manifest.permissions || [];
    return (
      <div className="flex flex-wrap gap-1">
        {permissions.map(permission => (
          <Badge key={permission} variant="outline" className="text-[11px]">
            <Shield className="mr-1 h-3 w-3" />
            {humanizePermission(permission)}
          </Badge>
        ))}
        {manifest.aiCapability && (
          <Badge variant="default" className="text-[11px]">
            <Sparkles className="mr-1 h-3 w-3" />
            MaplePT AI
          </Badge>
        )}
      </div>
    );
  };

  const logMarketplaceLaunch = useCallback(
    async (manifest: ModuleManifest) => {
      if (!user || !manifest.marketplace?.sku) {
        return;
      }

      await (supabase.from('marketplace_installs') as any).insert({
        user_id: user.id,
        sku: manifest.marketplace.sku,
        version: manifest.version,
        action: 'launch',
        user_agent: navigator.userAgent,
      });
    },
    [user],
  );

  const loadBundle = useCallback(
    (html: string, mod: InstalledModule) => {
      setActiveModuleId(mod.id);
      setActiveHtml(injectDesktopBridge(html));
      setIsRunning(true);
      setRuntimeKind('web');
      setWasmExports(null);
      setWasmLogs([]);
      setActiveWasmPath(null);
      void logMarketplaceLaunch(mod.manifest);
    },
    [injectDesktopBridge, logMarketplaceLaunch],
  );

  const loadPackage = useCallback(
    (vfsPath: string, mod: InstalledModule) => {
      const htmlEntry = mod.manifest.entry?.html || 'index.html';
      const htmlPath = `${vfsPath}\\${htmlEntry}`.replace(/\//g, '\\');
      const file = getFile(htmlPath);

      if (!file?.content) {
        toast({
          title: 'Missing package entry',
          description: `Cannot find ${htmlEntry} in the package.`,
          variant: 'destructive',
        });
        return false;
      }

      loadBundle(file.content, mod);
      return true;
    },
    [getFile, loadBundle, toast],
  );

  const handleInstall = async (mod: InstalledModule) => {
    if (mod.installed) {
      toast({
        title: 'Already installed',
        description: `${mod.manifest.name} is already installed.`,
      });
      return;
    }

    const targetRoot = `${PROGRAM_FILES_MODULES}\\${mod.manifest.name}`;
    try {
      await createFile(targetRoot, '', true);
      await ensureDir(targetRoot);

      const projectFiles = listFiles(mod.rootPath);
      for (const item of projectFiles) {
        const destinationPath = item.file_path.replace(mod.rootPath, targetRoot).replace(/\//g, '\\');
        if (item.is_directory) {
          await createFile(destinationPath, '', true);
          await ensureDir(destinationPath);
        } else {
          const fileData = getFile(item.file_path);
          const content = fileData?.content || '';
          const contentType = fileData?.content_type;
          await createFile(destinationPath, content, false, contentType);
          await ensureFile(destinationPath, content);
        }
      }

      toast({
        title: 'Installed',
        description: `${mod.manifest.name} has been installed to Program Files.`,
      });
      loadInstalledModules();
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: targetRoot } }));
    } catch {
      toast({
        title: 'Install failed',
        description: 'Could not install the module.',
        variant: 'destructive',
      });
    }
  };

  const resolveWasmEntry = useCallback(
    async (mod: InstalledModule) => {
      const preferred = mod.manifest.entry?.wasm ? [`${mod.rootPath}\\${mod.manifest.entry.wasm}`] : [];
      const candidates = [
        ...preferred,
        `${mod.rootPath}\\build\\module.wasm`,
        `${mod.rootPath}\\build\\module.wat`,
        `${mod.rootPath}\\src\\build\\module.wasm`,
        `${mod.rootPath}\\src\\build\\module.wat`,
        `${mod.rootPath}\\src\\module.wasm`,
        `${mod.rootPath}\\src\\module.wat`,
      ].map(normalizePath);

      for (const candidate of candidates) {
        const existing = getFile(candidate);
        if (existing?.content?.trim()) {
          return { path: candidate, file: existing };
        }

        const reloaded = await reloadFile(candidate);
        if (reloaded?.content?.trim()) {
          return { path: candidate, file: reloaded };
        }
      }

      return null;
    },
    [getFile, normalizePath, reloadFile],
  );

  const handleRun = useCallback(
    async (mod: InstalledModule) => {
      const type = mod.manifest.type;
      const runId = runCounterRef.current + 1;
      runCounterRef.current = runId;
      mirroredRunIdRef.current = null;
      warnedOutOfDateRef.current = false;

      if (type === 'web') {
        const loader = mod.manifest.loader || 'bundle';
        const htmlEntry = mod.manifest.entry?.html || 'index.html';
        const htmlPath = `${mod.rootPath}\\${htmlEntry}`.replace(/\//g, '\\');
        const file = getFile(htmlPath);

        if (loader === 'package') {
          const loaded = loadPackage(mod.rootPath, mod);
          if (!loaded) {
            return;
          }
        } else {
          if (!file?.content) {
            toast({
              title: 'Missing entry',
              description: `Cannot find ${htmlEntry} in the module.`,
              variant: 'destructive',
            });
            return;
          }

          loadBundle(file.content, mod);
        }

        toast({
          title: 'Module running',
          description: `${mod.manifest.name} is running.`,
        });
        return;
      }

      if (type === 'engine-plugin') {
        toast({
          title: 'Engine plugin',
          description: 'Please use MapleEngine to run plugin modules.',
        });
        return;
      }

      if (type !== 'wasm') {
        return;
      }

      const resolved = await resolveWasmEntry(mod);
      if (!resolved) {
        const missingLines = [
          `WASM error: Could not find ${mod.manifest.entry?.wasm || 'build\\module.wasm'} or a matching .wat source file.`,
          'See Module Host logs for details.',
        ];

        setActiveModuleId(mod.id);
        setRuntimeKind('wasm');
        setIsRunning(false);
        setActiveHtml('');
        setActiveWasmPath(null);
        setWasmExports(null);
        setWasmLogs([missingLines[0]]);

        toast({
          title: 'WASM not found',
          description: 'Cannot find a runnable WASM/WAT file for this module.',
          variant: 'destructive',
        });
        await mirrorRunSummaryToTerminal(mod, missingLines, runId);
        publishWasmRunResult(mod, 'error', missingLines, runId);
        return;
      }

      const { path: sourcePath, file } = resolved;
      if (!file?.content?.trim()) {
        const emptyLines = ['WASM error: Cannot find runnable module content.', 'See Module Host logs for details.'];

        toast({
          title: 'WASM not found',
          description: 'Cannot find runnable module content.',
          variant: 'destructive',
        });
        publishWasmRunResult(mod, 'error', emptyLines, runId, sourcePath);
        return;
      }

      setActiveModuleId(mod.id);
      setRuntimeKind('wasm');
      setIsRunning(false);
      setActiveHtml('');
      setActiveWasmPath(sourcePath);
      setWasmExports(null);
      setWasmLogs([]);

      const raw = file.content.trim();
      const summaryLines: string[] = [];
      try {
        let bytes: Uint8Array;
        let builtWasmPath: string | null = null;
        const isWatSource = sourcePath.toLowerCase().endsWith('.wat') || raw.startsWith('(module');
        const appendSummaryLine = (message: string) => {
          summaryLines.push(message);
        };
        const logRuntimeLine = (message: string) => {
          log(message);
          appendSummaryLine(message);
        };

        if (isWatSource) {
          bytes = await compileWatToWasm(raw);
          builtWasmPath = sourcePath.replace(/\.wat$/i, '.wasm');
          await writeModuleFile(builtWasmPath, bytesToBase64(bytes), 'application/wasm');
          log(`Built ${getRelativePath(mod.rootPath, builtWasmPath)} (${bytes.byteLength} bytes)`);
        } else {
          bytes = decodeBase64(raw);
          log(`Loaded ${getRelativePath(mod.rootPath, sourcePath)} (${bytes.byteLength} bytes)`);
        }

        log(`Using source: ${getRelativePath(mod.rootPath, sourcePath)}`);

        let exportedMemory: WebAssembly.Memory | null = null;
        const decoder = new TextDecoder();
        const readUtf8 = (pointer: number, length: number) => {
          if (!exportedMemory) {
            return '[host log failed: memory export not found]';
          }

          const start = pointer >>> 0;
          const size = length >>> 0;
          const bytesView = new Uint8Array(exportedMemory.buffer);
          const end = start + size;

          if (end > bytesView.byteLength) {
            return `[host log failed: invalid UTF-8 range ${start}-${end}]`;
          }

          return decoder.decode(bytesView.subarray(start, end));
        };

        const imports: WebAssembly.Imports = {
          env: {
            abort(_msg: number, _file: number, _line: number, _col: number) {
              log('WASM abort');
            },
            log_utf8(pointer: number, length: number) {
              logRuntimeLine(readUtf8(pointer, length));
            },
            print_utf8(pointer: number, length: number) {
              logRuntimeLine(readUtf8(pointer, length));
            },
            printf(pointer: number, length: number) {
              logRuntimeLine(readUtf8(pointer, length));
            },
            log_i32(value: number) {
              logRuntimeLine(String(value | 0));
            },
          },
          host: {
            log_utf8(pointer: number, length: number) {
              logRuntimeLine(readUtf8(pointer, length));
            },
            print_utf8(pointer: number, length: number) {
              logRuntimeLine(readUtf8(pointer, length));
            },
            printf(pointer: number, length: number) {
              logRuntimeLine(readUtf8(pointer, length));
            },
            log_i32(value: number) {
              logRuntimeLine(String(value | 0));
            },
          },
        };

        const result = (await WebAssembly.instantiate(bytes, imports)) as WebAssembly.Instance | WebAssembly.WebAssemblyInstantiatedSource;
        const exportsObject = ('instance' in result ? result.instance.exports : result.exports) as Record<string, any>;
        exportedMemory =
          Object.values(exportsObject).find(value => value instanceof WebAssembly.Memory) ?? null;

        const exportNames = Object.keys(exportsObject);
        appendSummaryLine(`${isWatSource ? 'Built' : 'Loaded'} ${getRelativePath(mod.rootPath, builtWasmPath || sourcePath)} (${bytes.byteLength} bytes)`);
        appendSummaryLine(`Using source: ${getRelativePath(mod.rootPath, sourcePath)}`);
        appendSummaryLine(`Exports: ${exportNames.length > 0 ? exportNames.join(', ') : 'none'}`);

        setWasmExports(exportsObject);
        setIsRunning(true);
        lastWasmSigRef.current = `${raw.length}:${raw[0] ?? ''}:${raw[raw.length - 1] ?? ''}`;

        const tryAutoRun = (exportName: '_start' | 'main' | 'hello') => {
          const candidate = exportsObject[exportName];
          if (typeof candidate !== 'function' || candidate.length > 0) {
            return false;
          }

          const resultValue = candidate();
          if (resultValue !== undefined) {
            logRuntimeLine(`${exportName}() => ${String(resultValue)}`);
          } else {
            logRuntimeLine(`Auto-ran ${exportName}()`);
          }
          return true;
        };

        if (!tryAutoRun('_start') && !tryAutoRun('main') && !tryAutoRun('hello')) {
          if (typeof exportsObject.add === 'function') {
            const addResult = exportsObject.add(1, 2);
            log(`add(1,2) = ${addResult}`);
            appendSummaryLine(`add(1,2) = ${addResult}`);
          } else {
            log('No auto-run entry found. Export _start(), main(), or hello() to run code automatically.');
            log('Use env.log_utf8(ptr,len), env.printf(ptr,len), or host.log_utf8(ptr,len) to print to the Module Host logs.');
            appendSummaryLine('No auto-run entry found. Export _start(), main(), or hello() to run code automatically.');
            appendSummaryLine('Use env.log_utf8(ptr,len), env.printf(ptr,len), or host.log_utf8(ptr,len) to print to the Module Host logs.');
          }
        }

        const tsPath = `${mod.rootPath}\\src\\main.ts`;
        const tsFile = getFile(tsPath);
        const sourceUpdatedAt = file.updated_at ? Date.parse(file.updated_at) : 0;
        const tsUpdatedAt = tsFile?.updated_at ? Date.parse(tsFile.updated_at) : 0;
        if (tsUpdatedAt && sourceUpdatedAt && tsUpdatedAt > sourceUpdatedAt && !warnedOutOfDateRef.current) {
          warnedOutOfDateRef.current = true;
          toast({
            title: 'Build is out of date',
            description: 'src/main.ts changed after the last build; rebuild build/module.(wasm|wat) to run your latest changes.',
          });
          log('Notice: src/main.ts is newer than the active WASM/WAT. Rebuild required for changes to take effect.');
          appendSummaryLine('Notice: src/main.ts is newer than the active WASM/WAT. Rebuild required for changes to take effect.');
        }

        await mirrorRunSummaryToTerminal(mod, summaryLines, runId);
        publishWasmRunResult(mod, 'success', summaryLines, runId, builtWasmPath || sourcePath);
      } catch (error: any) {
        const message = error?.message ?? String(error);
        const errorSummary = [...summaryLines, `WASM error: ${message}`, 'See Module Host logs for details.'];

        log(`WASM error: ${message}`);
        setIsRunning(false);
        setWasmExports(null);
        await mirrorRunSummaryToTerminal(mod, errorSummary, runId);
        publishWasmRunResult(mod, 'error', errorSummary, runId, sourcePath);
      }
    },
    [compileWatToWasm, decodeBase64, getFile, getRelativePath, loadBundle, loadPackage, log, mirrorRunSummaryToTerminal, publishWasmRunResult, resolveWasmEntry, toast, writeModuleFile],
  );

  useEffect(() => {
    if (!isLoaded || hasAutoRanRef.current || !initialProjectPath || activeModuleId) {
      return;
    }

    const targetId = initialPathIsInstalledModule
      ? normalizePath(initialProjectPath)
      : `${normalizePath(initialProjectPath)}::dev`;
    const targetModule = modules.find(module => module.id === targetId);
    if (!targetModule) {
      return;
    }

    hasAutoRanRef.current = true;
    void handleRun(targetModule);
  }, [activeModuleId, handleRun, initialPathIsInstalledModule, initialProjectPath, isLoaded, modules, normalizePath]);

  const handleStop = () => {
    setIsRunning(false);
    setActiveModuleId(null);
    setActiveHtml('');
    setRuntimeKind(null);
    setWasmExports(null);
    setWasmLogs([]);
    setActiveWasmPath(null);
    toast({
      title: 'Module stopped',
      description: 'The running module has been stopped.',
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isRunning || !activeModuleId) {
        return;
      }

      const activeModule = modules.find(module => module.id === activeModuleId);
      if (!activeModule) {
        return;
      }

      if (runtimeKind === 'web') {
        const htmlEntry = activeModule.manifest.entry?.html || 'src/index.html';
        const htmlPath = `${activeModule.rootPath}\\${htmlEntry}`;
        const file = getFile(htmlPath);
        const nextHtml = file?.content || '';
        const nextInjectedHtml = nextHtml ? injectDesktopBridge(nextHtml) : '';
        if (nextInjectedHtml && nextInjectedHtml !== activeHtml) {
          setActiveHtml(nextInjectedHtml);
        }
      }

      if (runtimeKind === 'wasm') {
        let sourceFilePath = activeWasmPath;
        let sourceFile = sourceFilePath ? getFile(sourceFilePath) : null;

        if (!sourceFilePath || !sourceFile?.content) {
          void (async () => {
            const resolved = await resolveWasmEntry(activeModule);
            if (resolved) {
              setActiveWasmPath(resolved.path);
            }
          })();
          return;
        }

        if (sourceFilePath && sourceFile?.content) {
          const content = sourceFile.content;
          const signature = `${content.length}:${content[0] ?? ''}:${content[content.length - 1] ?? ''}`;
          if (signature !== lastWasmSigRef.current) {
            lastWasmSigRef.current = signature;
            void handleRun(activeModule);
          }

          const tsPath = `${activeModule.rootPath}\\src\\main.ts`;
          const tsFile = getFile(tsPath);
          const wasmUpdatedAt = sourceFile.updated_at ? Date.parse(sourceFile.updated_at) : 0;
          const tsUpdatedAt = tsFile?.updated_at ? Date.parse(tsFile.updated_at) : 0;
          if (tsUpdatedAt && wasmUpdatedAt && tsUpdatedAt > wasmUpdatedAt && !warnedOutOfDateRef.current) {
            warnedOutOfDateRef.current = true;
            toast({
              title: 'Build is out of date',
              description: 'src/main.ts changed after the last build; rebuild build/module.(wasm|wat) to run your latest changes.',
            });
            log('Notice: src/main.ts is newer than the active WASM/WAT. Rebuild required for changes to take effect.');
          }
        }
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [activeHtml, activeModuleId, activeWasmPath, getFile, handleRun, injectDesktopBridge, isRunning, log, modules, resolveWasmEntry, runtimeKind, toast]);

  const showWebPreview = runtimeKind === 'web' && Boolean(activeHtml);
  const showWasmPreview = runtimeKind === 'wasm';

  return (
    <div className="h-full min-h-0 w-full overflow-hidden p-2 md:p-3">
      <div className="grid h-full min-h-0 grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] gap-3 md:grid-cols-3 md:grid-rows-1">
        <Card className="flex min-h-0 flex-col gap-3 overflow-hidden p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Installed Modules</h3>
            </div>
            <Badge variant="outline">System-wide</Badge>
          </div>

          <Separator />

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-7"
                placeholder="Search modules..."
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
              />
            </div>

            <label className="text-xs text-muted-foreground">Category</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {allCategories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ScrollArea className="min-h-0 flex-1">
            <div className="space-y-2 pr-2">
              {filtered.map(mod => (
                <Card key={mod.id} className={`p-3 ${activeModuleId === mod.id ? 'border-primary' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                      <Cpu className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{mod.manifest.name}</div>
                          <div className="text-xs text-muted-foreground">
                            v{mod.manifest.version} · {mod.installed ? 'Installed' : 'Dev project'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!mod.installed && (
                            <Button
                              aria-label="Delete project"
                              title="Delete project"
                              variant="destructive"
                              size="icon"
                              onClick={event => {
                                event.stopPropagation();
                                requestDeleteModule(mod);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                          {!mod.installed && (
                            <Button size="sm" onClick={() => void handleInstall(mod)}>
                              <Upload className="mr-1 h-4 w-4" />
                              Install
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => void handleRun(mod)}>
                            <Play className="mr-1 h-4 w-4" />
                            Run
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        {mod.manifest.description || 'No description provided.'}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(mod.manifest.categories || []).map(category => (
                          <Badge key={category} variant="secondary" className="text-[11px]">
                            <Globe className="mr-1 h-3 w-3" />
                            {category}
                          </Badge>
                        ))}
                      </div>
                      <div className="mt-2">{renderPermissionBadges(mod.manifest)}</div>
                      {mod.manifest.permissions?.includes('files:write') && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          This module can write files. You will be prompted when it accesses user files.
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}

              {filtered.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  No modules found. Install modules or open a project with a manifest.json.
                </div>
              )}
            </div>
          </ScrollArea>
        </Card>

        <Card className="md:col-span-2 flex min-h-0 flex-col overflow-hidden p-0">
          <div className="flex items-center justify-between border-b bg-muted/30 px-3 py-2">
            <div className="text-sm text-muted-foreground">Preview</div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Mirror to Terminal</span>
                <Switch checked={mirrorToTerminal} onCheckedChange={setMirrorToTerminal} aria-label="Mirror to Terminal" />
              </div>
              {isRunning ? (
                <Badge variant="default">
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Running
                </Badge>
              ) : (
                <Badge variant="outline">Idle</Badge>
              )}
              {(isRunning || showWasmPreview || showWebPreview) && (
                <Button variant="ghost" size="sm" onClick={handleStop}>
                  <StopCircle className="mr-1 h-4 w-4" />
                  Stop
                </Button>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1">
            {showWebPreview ? (
              <iframe
                ref={iframeRef}
                title="Module Preview"
                className="h-full w-full border-0"
                srcDoc={activeHtml}
                sandbox="allow-scripts allow-same-origin allow-modals"
              />
            ) : showWasmPreview ? (
              <div className="flex h-full min-h-0 flex-col overflow-hidden p-3">
                <div className="mb-2 text-sm font-medium">WASM Exports</div>
                <div className="mb-3">
                  {wasmExports ? (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {Object.keys(wasmExports).map(key => (
                        <Badge key={key} variant="secondary">
                          {key}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">No exports found.</div>
                  )}
                </div>

                {wasmExports && typeof wasmExports.add === 'function' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mb-3 w-fit"
                    onClick={() => {
                      try {
                        const result = wasmExports.add(1, 2);
                        log(`add(1,2) = ${result}`);
                      } catch (error: any) {
                        log(`Error calling add: ${error?.message ?? error}`);
                      }
                    }}
                  >
                    Test add(1,2)
                  </Button>
                )}

                <div className="mb-2 text-sm font-medium">Logs</div>
                <div className="min-h-0 flex-1 overflow-auto rounded bg-muted p-2 font-mono text-xs">
                  {wasmLogs.length === 0 ? (
                    <div className="text-muted-foreground">No logs yet.</div>
                  ) : (
                    wasmLogs.map((entry, index) => <div key={index}>{entry}</div>)
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                Select a module and click Run to preview it here.
              </div>
            )}
          </div>
        </Card>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="z-[9999]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              {moduleToDelete ? `This will remove ${moduleToDelete.manifest.name} and all of its files.` : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void performDeleteModule()} disabled={isDeleting}>
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModuleHost;